"use client";

import { CSSProperties, MouseEvent, ReactNode, useState } from "react";

export default function SparkleEffect({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  const [sparkles, setSparkles] = useState<
    {
      id: string;
      x: number;
      y: number;
      color: string;
      angle: number;
      size: number;
      rotation: number;
      duration: number;
    }[]
  >([]);

  const createSparkle = (e: MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const colors = [
      "#ff0000",
      "#00ff00",
      "#ffd700",
      "#ffffff",
      "#ff69b4",
      "#00ffff",
    ];
    const id = Date.now() + Math.random();

    const newSparkles = Array.from({ length: 12 }, (_, i) => ({
      id: `${id}-${i}`,
      x,
      y,
      color: colors[Math.floor(Math.random() * colors.length)],
      angle: (360 / 12) * i,
      size: Math.random() * 12 + 16,
      rotation: Math.random() * 360,
      duration: Math.random() * 0.5 + 0.5,
    }));

    setSparkles((prev) => [...prev, ...newSparkles]);

    setTimeout(() => {
      setSparkles((prev) =>
        prev.filter((s) => !s.id.toString().startsWith(id.toString())),
      );
    }, 1000);
  };

  return (
    <div className={`relative ${className}`} onClick={createSparkle}>
      {children}

      {sparkles.map((sparkle) => {
        const distance = 120;
        const radians = (sparkle.angle * Math.PI) / 180;
        const endX = sparkle.x + Math.cos(radians) * distance;
        const endY = sparkle.y + Math.sin(radians) * distance;

        return (
          <div
            key={sparkle.id}
            className="absolute pointer-events-none z-50"
            style={
              {
                left: sparkle.x,
                top: sparkle.y,
                animation: `sparkleMove ${sparkle.duration}s ease-out forwards`,
                "--end-x": `${endX - sparkle.x}px`,
                "--end-y": `${endY - sparkle.y}px`,
              } as CSSProperties
            }
          >
            <svg
              width={sparkle.size}
              height={sparkle.size}
              viewBox="0 0 24 24"
              style={
                {
                  filter: `drop-shadow(0 0 ${sparkle.size / 2}px ${sparkle.color})`,
                  animation: `sparkleFade ${sparkle.duration}s ease-out forwards, sparkleRotate ${sparkle.duration}s linear forwards`,
                  "--rotation": `${sparkle.rotation}deg`,
                } as CSSProperties
              }
            >
              <path
                d="M12 2L14.5 9.5L22 12L14.5 14.5L12 22L9.5 14.5L2 12L9.5 9.5L12 2Z"
                fill={sparkle.color}
                stroke={sparkle.color}
                strokeWidth="0.5"
              />
            </svg>
          </div>
        );
      })}

      <style>{`
        @keyframes sparkleMove {
          0% {
            transform: translate(0, 0) scale(1);
          }
          100% {
            transform: translate(var(--end-x), var(--end-y)) scale(0.3);
          }
        }

        @keyframes sparkleFade {
          0% {
            opacity: 1;
          }
          70% {
            opacity: 0.8;
          }
          100% {
            opacity: 0;
          }
        }

        @keyframes sparkleRotate {
          0% {
            transform: rotate(var(--rotation));
          }
          100% {
            transform: rotate(calc(var(--rotation) + 180deg));
          }
        }
      `}</style>
    </div>
  );
}
